import { Controller } from "@hotwired/stimulus";

/**
 * Routing Animation Controller
 *
 * Creates an animated flow diagram showing intelligent routing with automatic fallback.
 * Animation shows: API Consumer → Lumenfall → Vertex (fails) → Gemini API (succeeds)
 * Starts when section enters viewport and loops after completion.
 */
export default class extends Controller<HTMLElement> {
  static targets = [
    "packet",
    "packetInner",
    "path1",
    "path2a",
    "path2b",
    "vertexStatus",
    "geminiStatus",
    "appNode",
    "lumenfallNode",
    "vertexNode",
    "geminiNode",
    "resultImage",
    "resultPath",
    "container",
    "connectionLine",
  ];

  declare readonly packetTarget: SVGGElement;
  declare readonly packetInnerTarget: SVGGElement;
  declare readonly path1Target: SVGPathElement;
  declare readonly path2aTarget: SVGPathElement;
  declare readonly path2bTarget: SVGPathElement;
  declare readonly vertexStatusTarget: SVGGElement;
  declare readonly geminiStatusTarget: SVGGElement;
  declare readonly appNodeTarget: SVGGElement;
  declare readonly lumenfallNodeTarget: SVGGElement;
  declare readonly vertexNodeTarget: SVGGElement;
  declare readonly geminiNodeTarget: SVGGElement;
  declare readonly resultImageTarget: SVGGElement;
  declare readonly resultPathTarget: SVGPathElement;
  declare readonly containerTarget: HTMLElement;
  declare readonly connectionLineTargets: SVGPathElement[];
  declare readonly hasPacketTarget: boolean;

  // SVG container height (fixed to prevent layout shift)
  // Result area visibility is controlled via opacity, not height changes
  private readonly containerHeight = "385px";

  private observer: IntersectionObserver | null = null;
  private animationTimeout: ReturnType<typeof setTimeout> | null = null;
  private isAnimating: boolean = false;
  private themeObserver: MutationObserver | null = null;

  connect(): void {
    this.initializeNodeColors();
    this.setupThemeObserver();
    this.setupIntersectionObserver();
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isAnimating) {
            this.startAnimation();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this.element);
  }

  private async startAnimation(): Promise<void> {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Reset state
    this.resetAnimation();

    // Brief delay before starting
    await this.pause(600);

    // Show the packet at starting position
    this.showPacket();

    // Phase 1: Packet travels from App to Lumenfall
    await this.animateAlongPath(this.path1Target, 600);

    // Dot enters: fade out packet and light up Lumenfall simultaneously
    this.fadeOutPacket();
    this.lightUpLumenfallNode();
    await this.pause(250); // Wait for fade/light-up transition

    // Brief pause at Lumenfall (while packet is hidden inside)
    await this.pause(200);

    // Position packet at start of path2a (exit point of Lumenfall) while still hidden
    const exitPoint = this.path2aTarget.getPointAtLength(0);
    this.packetTarget.setAttribute("transform", `translate(${exitPoint.x}, ${exitPoint.y})`);

    // Dot exits: fade in packet and dim Lumenfall simultaneously
    this.fadeInPacket();
    this.dimLumenfallNode();
    await this.pause(250); // Wait for fade/dim transition

    // Phase 2a: Packet travels to Vertex (normal speed)
    await this.animateAlongPath(this.path2aTarget, 800);

    // Show Vertex failure
    await this.showVertexFailure();

    // Brief pause to show failure
    await this.pause(500);

    // Phase 2b: Packet quickly reroutes to Gemini (fast)
    await this.animateAlongPath(this.path2bTarget, 400);

    // Show Gemini success
    await this.showGeminiSuccess();

    // Show the result
    await this.pause(400);
    await this.showResult();

    // Wait and then loop
    await this.pause(3500);

    this.isAnimating = false;

    // Loop the animation
    this.animationTimeout = setTimeout(() => {
      this.startAnimation();
    }, 800);
  }

  private isDarkMode(): boolean {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }
    const savedTheme = localStorage.getItem("lumenTheme");
    if (savedTheme === "dark") return true;
    if (savedTheme === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getNodeFillColor(): string {
    return this.isDarkMode() ? "oklch(30% 0.01 90 / 0.5)" : "oklch(88.7% 0.01 90 / 0.5)";
  }

  private getNodeStrokeColor(): string {
    return this.isDarkMode() ? "oklch(50% 0.01 89 / 0.3)" : "oklch(34% 0.01 89 / 0.3)";
  }

  private getIconColor(): string {
    return this.isDarkMode() ? "oklch(80% 0.01 89 / 0.7)" : "oklch(34% 0.01 89 / 0.5)";
  }

  private getIconFillColor(): string {
    return this.isDarkMode() ? "oklch(80% 0.01 89 / 0.5)" : "oklch(34% 0.01 89 / 0.3)";
  }

  private getLineColor(): string {
    return this.isDarkMode() ? "oklch(70% 0.01 89 / 0.35)" : "oklch(34% 0.01 89 / 0.25)";
  }

  private getTextColor(): string {
    return this.isDarkMode() ? "oklch(85% 0.01 90 / 0.8)" : "oklch(34% 0.01 89 / 0.8)";
  }

  private getTextMutedColor(): string {
    return this.isDarkMode() ? "oklch(85% 0.01 90 / 0.6)" : "oklch(34% 0.01 89 / 0.6)";
  }

  private getPacketCenterColor(): string {
    return this.isDarkMode() ? "oklch(85% 0.01 90 / 0.8)" : "oklch(34% 0.01 89 / 0.8)";
  }

  private initializeNodeColors(): void {
    const nodeFill = this.getNodeFillColor();
    const nodeStroke = this.getNodeStrokeColor();
    const iconColor = this.getIconColor();
    const iconFillColor = this.getIconFillColor();
    const textColor = this.getTextColor();
    const textMutedColor = this.getTextMutedColor();

    // Apply colors to all provider nodes (App, Vertex, Gemini)
    const appRect = this.appNodeTarget.querySelector("rect");
    const vertexRect = this.vertexNodeTarget.querySelector("rect");
    const geminiRect = this.geminiNodeTarget.querySelector("rect");

    if (appRect) {
      appRect.setAttribute("fill", nodeFill);
      appRect.setAttribute("stroke", nodeStroke);
    }
    if (vertexRect) {
      vertexRect.setAttribute("fill", nodeFill);
      vertexRect.setAttribute("stroke", nodeStroke);
    }
    if (geminiRect) {
      geminiRect.setAttribute("fill", nodeFill);
      geminiRect.setAttribute("stroke", nodeStroke);
    }

    // Update text colors for all nodes
    const appText = this.appNodeTarget.querySelector("text");
    const vertexText = this.vertexNodeTarget.querySelector("text");
    const geminiText = this.geminiNodeTarget.querySelector("text");
    if (appText) appText.setAttribute("fill", textColor);
    if (vertexText) vertexText.setAttribute("fill", textColor);
    if (geminiText) geminiText.setAttribute("fill", textColor);

    // Update Lumenfall text colors
    const lumenfallTexts = this.lumenfallNodeTarget.querySelectorAll("text");
    lumenfallTexts.forEach((text, index) => {
      // First text is the title (brighter), second is subtitle (muted)
      text.setAttribute("fill", index === 0 ? textColor : textMutedColor);
    });

    // Update icon colors for App node (code brackets)
    const appIcon = this.appNodeTarget.querySelector("g path");
    if (appIcon) {
      appIcon.setAttribute("stroke", iconColor);
    }

    // Update result image icon colors
    const resultIconGroup = this.resultImageTarget.querySelector("g");
    if (resultIconGroup) {
      const iconRect = resultIconGroup.querySelector("rect");
      const iconCircle = resultIconGroup.querySelector("circle");
      const iconPath = resultIconGroup.querySelector("path");
      if (iconRect) iconRect.setAttribute("stroke", iconColor);
      if (iconCircle) iconCircle.setAttribute("fill", iconColor);
      if (iconPath) iconPath.setAttribute("fill", iconFillColor);
    }

    // Update connection line colors
    const lineColor = this.getLineColor();
    this.connectionLineTargets.forEach((line) => {
      line.setAttribute("stroke", lineColor);
    });

    // Also update the result path
    this.resultPathTarget.setAttribute("stroke", lineColor);

    // Update packet center color
    const packetCenter = this.packetInnerTarget.querySelector("circle:last-child");
    if (packetCenter) {
      packetCenter.setAttribute("fill", this.getPacketCenterColor());
    }
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          // Always update colors on theme change - the color getters check isDarkMode() each time
          this.initializeNodeColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  private resetAnimation(): void {
    // Hide packet and position off-screen
    this.packetTarget.style.opacity = "0";
    this.packetTarget.setAttribute("transform", "translate(-100, -100)");

    // Reset status indicators
    this.vertexStatusTarget.classList.remove("opacity-100");
    this.vertexStatusTarget.classList.add("opacity-0");
    this.geminiStatusTarget.classList.remove("opacity-100");
    this.geminiStatusTarget.classList.add("opacity-0");

    // Reset node highlights - remove any ring classes
    const appRect = this.appNodeTarget.querySelector("rect");
    const vertexRect = this.vertexNodeTarget.querySelector("rect");
    const geminiRect = this.geminiNodeTarget.querySelector("rect");
    const nodeFill = this.getNodeFillColor();
    const nodeStroke = this.getNodeStrokeColor();

    if (appRect) {
      appRect.setAttribute("fill", nodeFill);
      appRect.setAttribute("stroke", nodeStroke);
      appRect.setAttribute("stroke-width", "1.5");
    }
    if (vertexRect) {
      vertexRect.setAttribute("fill", nodeFill);
      vertexRect.setAttribute("stroke", nodeStroke);
      vertexRect.setAttribute("stroke-width", "1.5");
    }
    // Reset Vertex text color
    const vertexText = this.vertexNodeTarget.querySelector("text");
    if (vertexText) {
      vertexText.setAttribute("fill", this.getTextColor());
    }
    if (geminiRect) {
      geminiRect.setAttribute("fill", nodeFill);
      geminiRect.setAttribute("stroke", nodeStroke);
      geminiRect.setAttribute("stroke-width", "1.5");
    }
    // Reset Gemini text color
    const geminiText = this.geminiNodeTarget.querySelector("text");
    if (geminiText) {
      geminiText.setAttribute("fill", this.getTextColor());
    }

    // Reset Lumenfall node
    const lumenfallRect = this.lumenfallNodeTarget.querySelector("rect");
    if (lumenfallRect) {
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.15)");
      lumenfallRect.setAttribute("stroke-width", "2");
    }

    // Hide result image and path
    this.resultImageTarget.classList.remove("opacity-100");
    this.resultImageTarget.classList.add("opacity-0");
    this.resultPathTarget.classList.remove("opacity-100");
    this.resultPathTarget.classList.add("opacity-0");
  }

  private showPacket(): void {
    // Position at start of path1
    const startPoint = this.path1Target.getPointAtLength(0);
    this.packetTarget.setAttribute(
      "transform",
      `translate(${startPoint.x}, ${startPoint.y})`
    );
    this.packetTarget.style.transition = "none";
    this.packetTarget.style.opacity = "1";
  }

  private animateAlongPath(
    path: SVGPathElement,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const length = path.getTotalLength();
      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth motion
        const easeProgress = this.easeInOutCubic(progress);

        // Get point along path
        const point = path.getPointAtLength(easeProgress * length);

        // Move packet
        this.packetTarget.setAttribute(
          "transform",
          `translate(${point.x}, ${point.y})`
        );

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private updateGradientColors(): void {
    const isDark = this.isDarkMode();
    const svg = this.element.querySelector("svg");
    if (!svg) return;

    // Update error gradient colors
    // Dark mode: darker red that works with light text
    // Light mode: pastel red that works with dark text
    const errorGradient = svg.querySelector("#errorNodeGradient");
    if (errorGradient) {
      const stops = errorGradient.querySelectorAll("stop");
      if (stops[0]) stops[0].setAttribute("style", `stop-color:${isDark ? "#991b1b" : "#fecaca"};stop-opacity:1`);
      if (stops[1]) stops[1].setAttribute("style", `stop-color:${isDark ? "#7f1d1d" : "#fca5a5"};stop-opacity:1`);
    }

    // Update success gradient colors
    // Dark mode: darker green that works with light text
    // Light mode: pastel green that works with dark text
    const successGradient = svg.querySelector("#successNodeGradient");
    if (successGradient) {
      const stops = successGradient.querySelectorAll("stop");
      if (stops[0]) stops[0].setAttribute("style", `stop-color:${isDark ? "#14532d" : "#bbf7d0"};stop-opacity:1`);
      if (stops[1]) stops[1].setAttribute("style", `stop-color:${isDark ? "#166534" : "#86efac"};stop-opacity:1`);
    }
  }

  private fadeOutPacket(): void {
    this.packetTarget.style.transition = "opacity 250ms ease-out";
    this.packetTarget.style.opacity = "0";
  }

  private fadeInPacket(): void {
    this.packetTarget.style.transition = "opacity 250ms ease-in";
    this.packetTarget.style.opacity = "1";
  }

  private lightUpLumenfallNode(): void {
    const lumenfallRect = this.lumenfallNodeTarget.querySelector("rect");
    if (lumenfallRect) {
      // Add CSS transition for smooth effect
      lumenfallRect.style.transition = "fill 250ms ease-out, stroke-width 250ms ease-out";
      // Increase fill opacity and stroke width
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.35)");
      lumenfallRect.setAttribute("stroke-width", "3");
    }
  }

  private dimLumenfallNode(): void {
    const lumenfallRect = this.lumenfallNodeTarget.querySelector("rect");
    if (lumenfallRect) {
      // Add CSS transition for smooth effect
      lumenfallRect.style.transition = "fill 250ms ease-in, stroke-width 250ms ease-in";
      // Return to original state
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.15)");
      lumenfallRect.setAttribute("stroke-width", "2");
    }
  }

  private async showVertexFailure(): Promise<void> {
    const isDark = this.isDarkMode();

    // Highlight vertex node with theme error color (warm coral-red)
    const vertexRect = this.vertexNodeTarget.querySelector("rect");
    if (vertexRect) {
      // Use theme error color with appropriate opacity
      vertexRect.setAttribute("fill", isDark ? "oklch(65% 0.2 25 / 0.25)" : "oklch(65% 0.2 25 / 0.35)");
      vertexRect.setAttribute("stroke", "oklch(65% 0.2 25)");
      vertexRect.setAttribute("stroke-width", "2");
    }

    // Update text color to match the error state
    const vertexText = this.vertexNodeTarget.querySelector("text");
    if (vertexText) {
      vertexText.setAttribute("fill", isDark ? "oklch(90% 0.05 25)" : "oklch(40% 0.15 25)");
    }

    // Show error status
    this.vertexStatusTarget.classList.remove("opacity-0");
    this.vertexStatusTarget.classList.add("opacity-100");

    // Shake the inner packet briefly (not the position group)
    this.packetInnerTarget.classList.add("animate-shake");
    await this.pause(300);
    this.packetInnerTarget.classList.remove("animate-shake");
  }

  private async showGeminiSuccess(): Promise<void> {
    const isDark = this.isDarkMode();

    // Highlight gemini node with theme primary color (lime/chartreuse)
    const geminiRect = this.geminiNodeTarget.querySelector("rect");
    if (geminiRect) {
      // Use theme primary color with appropriate opacity
      geminiRect.setAttribute("fill", isDark ? "oklch(93.0% 0.147 126.0 / 0.2)" : "oklch(93.0% 0.147 126.0 / 0.35)");
      geminiRect.setAttribute("stroke", "oklch(93.0% 0.147 126.0)");
      geminiRect.setAttribute("stroke-width", "2");
    }

    // Update text color to match the success state (dark text on light green background)
    const geminiText = this.geminiNodeTarget.querySelector("text");
    if (geminiText) {
      geminiText.setAttribute("fill", isDark ? "oklch(95% 0.05 126)" : "oklch(35% 0.1 126)");
    }

    // Show success status
    this.geminiStatusTarget.classList.remove("opacity-0");
    this.geminiStatusTarget.classList.add("opacity-100");

    // Brief pulse effect
    await this.pause(200);
  }

  private async showResult(): Promise<void> {
    // Hide packet
    this.packetTarget.style.opacity = "0";

    // Show result path and image with animation (no height change to prevent layout shift)
    this.resultPathTarget.classList.remove("opacity-0");
    this.resultPathTarget.classList.add("opacity-100");
    this.resultImageTarget.classList.remove("opacity-0");
    this.resultImageTarget.classList.add("opacity-100");
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
